import { z } from 'zod';

import { PAGINATION } from '../constants/index.js';

import { HotelBookedRoomSchema } from './hotel-booking.js';
import { paginatedResponseSchema } from './pagination.js';
import {
  CancellationReasonCodeSchema,
  TravelCancellationQuoteSchema,
} from './travel-cancellation.js';

/**
 * The admin panel's view over website flight and hotel bookings.
 *
 * One contract for both products on purpose: an operator working the
 * "action needed" queue does not care which funnel a booking came through,
 * and two near-identical list screens is how they drift apart. The
 * product-specific halves (`flight`, `hotel`) are nullable blocks on the
 * detail, exactly as the notification payload does it.
 *
 * Document numbers (passport, PAN) never travel in a list or a detail - only
 * a masked form does. The full value is a separate, audited read
 * (`TravelBookingAdminPassportRevealSchema`) behind an explicit click.
 */

export const TravelBookingAdminProductSchema = z.enum(['FLIGHT', 'HOTEL']);
export type TravelBookingAdminProduct = z.infer<typeof TravelBookingAdminProductSchema>;

/** Mirrors the Prisma enum, in lifecycle order. */
export const TravelBookingAdminStatusSchema = z.enum([
  'DRAFT',
  'PRICED',
  'PAX_ENTERED',
  'PAYMENT_PENDING',
  'PAID',
  'BOOKING',
  'ON_HOLD',
  'CONFIRMED',
  'BOOK_FAILED',
  'CANCELLATION_REQUESTED',
  'CANCELLED',
  'REFUNDED',
  'EXPIRED',
  /** Non-LCC flight fare locked with a deposit; balance owed before ticketing. */
  'HELD',
]);
export type TravelBookingAdminStatus = z.infer<typeof TravelBookingAdminStatusSchema>;

/**
 * Coarse views an operator actually works from.
 *
 * The Ticketing pages each pin one:
 *  - `handled` - everything that passed through an admin's hands: on hold
 *    now, or ever held, cancelled by an admin, refunded by an admin. The
 *    "Action needed" page, with a status filter on top.
 *  - `booked` - bookings that really were made with the supplier: confirmed,
 *    and whatever a confirmed booking later became (cancellation requested,
 *    cancelled, refunded). A partly issued flight counts - a ticket exists.
 *  - `dropoff` - checkouts the guest walked away from after telling us who
 *    they are (email and phone saved), never paid, now expired.
 *
 * `action` is the bare hold queue; the rest are derived from status + travel
 * date so "upcoming" and "completed" are never a status somebody has to
 * remember to set.
 */
export const TravelBookingAdminBucketSchema = z.enum([
  'all',
  'action',
  'handled',
  'booked',
  'dropoff',
  'upcoming',
  'completed',
  'cancelled',
  'failed',
]);
export type TravelBookingAdminBucket = z.infer<typeof TravelBookingAdminBucketSchema>;

const IsoDateOnly = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use YYYY-MM-DD');

/** Accept `?status=A&status=B`, `?status=A,B` and a single value alike. */
const StatusListSchema = z.preprocess((v) => {
  if (v === undefined || v === null || v === '') return undefined;
  const raw = Array.isArray(v) ? v : String(v).split(',');
  const list = raw.map((s) => String(s).trim()).filter((s) => s.length > 0);
  return list.length > 0 ? list : undefined;
}, z.array(TravelBookingAdminStatusSchema).optional());

export const TravelBookingAdminSortBySchema = z.enum([
  'createdAt',
  'travelStartDate',
  'sellPaise',
  'status',
  'reference',
  'holdExpiresAt',
  'markupPaise',
  'tboMarkupMinor',
]);

export const TravelBookingAdminListQuerySchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  // The shared cap, not a local one: the admin "Export to Excel" pages at
  // `PAGINATION.MAX_PAGE_SIZE`, and a list that accepts less than that
  // answers every export with a 400.
  pageSize: z.coerce.number().int().positive().max(PAGINATION.MAX_PAGE_SIZE).default(20),
  sortBy: TravelBookingAdminSortBySchema.default('createdAt'),
  sortDir: z.enum(['asc', 'desc']).default('desc'),
  /** Reference, PNR / confirmation number, traveller name, email or phone. */
  q: z.string().trim().max(120).optional(),
  product: TravelBookingAdminProductSchema.optional(),
  status: StatusListSchema,
  bucket: TravelBookingAdminBucketSchema.default('all'),
  /** Booking creation window (inclusive), YYYY-MM-DD. */
  createdFrom: IsoDateOnly.optional(),
  createdTo: IsoDateOnly.optional(),
  /** Travel start window (inclusive), YYYY-MM-DD. */
  travelFrom: IsoDateOnly.optional(),
  travelTo: IsoDateOnly.optional(),
});
export type TravelBookingAdminListQuery = z.infer<typeof TravelBookingAdminListQuerySchema>;

const Paise = z.string().regex(/^-?\d+$/);

export const TravelBookingAdminListItemSchema = z.object({
  id: z.string(),
  reference: z.string(),
  product: TravelBookingAdminProductSchema,
  status: TravelBookingAdminStatusSchema,
  /** "Delhi to Mumbai" / the hotel name. */
  title: z.string(),
  /** "IndiGo, 2 travellers" / "3 nights, 1 room". */
  subtitle: z.string(),
  leadName: z.string(),
  contactEmail: z.string(),
  contactPhone: z.string(),
  travellerCount: z.number().int(),
  travelStartDate: z.string().nullable(),
  travelEndDate: z.string().nullable(),
  /** Airline PNR or hotel confirmation number, once one exists. */
  supplierRef: z.string().nullable(),
  sellPaise: Paise,
  refundedPaise: Paise,
  absorbedDeltaPaise: Paise,
  /** The markup line of the price, as stored on the booking. */
  markupPaise: Paise,
  /**
   * TBO's own margin on us: what it actually took off the agency wallet, less
   * the net it quoted when we priced the booking.
   *
   * **In `supplierCurrency`, not `currencyCode`** - both sides of the
   * subtraction come off the supplier's price block, which is quoted in the
   * supplier's own currency. A screen that renders this with the booking's
   * currency will print "₹" over a figure that is not rupees on every
   * international stay.
   *
   * Null means we were never told, not that the variance was zero: the supplier
   * response carried no price block, or the booking predates the capture. Zero
   * is a real and different answer - TBO settled at exactly what it quoted.
   */
  tboMarkupMinor: Paise.nullable().default(null),
  /** What TBO settled at, in `supplierCurrency`. Null on the same terms. */
  actualSupplierNetMinor: Paise.nullable().default(null),
  /**
   * The supplier's PUBLISHED fare, in `supplierCurrency`. Null on the same
   * terms. `supplierPublishedMinor - actualSupplierNetMinor` is the agency
   * discount / margin TBO gives us on the fare.
   */
  supplierPublishedMinor: Paise.nullable().default(null),
  /**
   * Our net profit on this booking, in paise, EXCLUDING GST: markup + the
   * agency discount TBO gives us + any add-on margin, LESS the payment-gateway
   * fee. Negative when the gateway fee outweighs a thin margin. Defaulted for
   * staggered-deploy safety like the fields above.
   */
  netProfitPaise: Paise.nullable().default(null),
  /**
   * The currency the two fields above are quoted in.
   *
   * **Defaulted rather than required, and the default is what makes a staggered
   * deploy safe.** The admin app `.parse()`s this response, so a required field
   * the API has not shipped yet is not a missing column - it is a thrown
   * ZodError that takes the whole Ticketing list down until both sides are on
   * the same version. Defaults make an older API's response parse cleanly and
   * simply render as "not known yet", which is exactly what it is.
   *
   * "INR" specifically because the value is only ever READ beside a non-null
   * variance, which an API that does not send this field cannot produce - and
   * because an empty string would throw inside `Intl.NumberFormat`.
   */
  supplierCurrency: z.string().default('INR'),
  currencyCode: z.string(),
  /** When the current hold began; null unless ON_HOLD. */
  holdSince: z.string().nullable(),
  /** The next admin reminder for the current hold; null unless ON_HOLD. */
  holdExpiresAt: z.string().nullable(),
  /**
   * A flight whose booking failed after at least one ticket was issued. It
   * belongs with the booked bookings (a ticket exists) and never with the
   * automatic refunds (what the guest is owed is a human decision).
   */
  isPartlyIssued: z.boolean(),
  /**
   * How far an abandoned checkout got before the guest left. Null on
   * anything that was paid for. `PAYMENT` means a gateway order was opened.
   */
  dropOffStage: z.enum(['DETAILS', 'PAYMENT']).nullable(),
  failureReason: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  paidAt: z.string().nullable(),
  confirmedAt: z.string().nullable(),
});
export type TravelBookingAdminListItem = z.infer<typeof TravelBookingAdminListItemSchema>;

export const TravelBookingAdminListResponseSchema = paginatedResponseSchema(
  TravelBookingAdminListItemSchema,
);
export type TravelBookingAdminListResponse = z.infer<typeof TravelBookingAdminListResponseSchema>;

export const TravelBookingAdminPassengerSchema = z.object({
  id: z.string(),
  paxIndex: z.number().int(),
  passengerType: z.string(),
  title: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  dateOfBirth: z.string().nullable(),
  gender: z.string().nullable(),
  nationality: z.string().nullable(),
  /** Last three characters only. See the file note. */
  passportNumberMasked: z.string().nullable(),
  passportExpiry: z.string().nullable(),
  passportIssuingCountry: z.string().nullable(),
  panNumberMasked: z.string().nullable(),
  isLeadPax: z.boolean(),
  ticketNumber: z.string().nullable(),
  seatAssigned: z.string().nullable(),
  mealAssigned: z.string().nullable(),
});
export type TravelBookingAdminPassenger = z.infer<typeof TravelBookingAdminPassengerSchema>;

export const TravelBookingAdminGuestSchema = z.object({
  id: z.string(),
  guestIndex: z.number().int(),
  guestType: z.string(),
  title: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  guestInRoom: z.number().int(),
  isLeadGuest: z.boolean(),
  age: z.number().int().nullable(),
  panNumberMasked: z.string().nullable(),
  passportNumberMasked: z.string().nullable(),
  passportExpiry: z.string().nullable(),
});
export type TravelBookingAdminGuest = z.infer<typeof TravelBookingAdminGuestSchema>;

export const TravelBookingAdminEventSchema = z.object({
  id: z.string(),
  kind: z.string(),
  fromStatus: z.string().nullable(),
  toStatus: z.string().nullable(),
  errorMessage: z.string().nullable(),
  /** Redacted supplier request/response, when the event recorded one. */
  detail: z.unknown().nullable(),
  createdAt: z.string(),
});
export type TravelBookingAdminEvent = z.infer<typeof TravelBookingAdminEventSchema>;

export const TravelBookingAdminDetailSchema = TravelBookingAdminListItemSchema.extend({
  contact: z.object({
    email: z.string(),
    phone: z.string(),
    addressLine: z.string().nullable(),
    city: z.string().nullable(),
    countryCode: z.string().nullable(),
  }),
  gst: z.record(z.string(), z.unknown()).nullable(),
  money: z.object({
    basePaise: Paise,
    taxPaise: Paise,
    ssrPaise: Paise,
    markupPaise: Paise,
    convenienceFeePaise: Paise,
    gstPaise: Paise,
    tcsPaise: Paise,
    /** Rounding to the whole rupee, derived server-side; 0 for older bookings. */
    roundOffPaise: Paise,
    sellPaise: Paise,
    refundedPaise: Paise,
    absorbedDeltaPaise: Paise,
    /** The last QUOTE we saw (base + tax, seats excluded). */
    supplierNetMinor: Paise,
    /** What the supplier ACTUALLY billed our wallet (airfare + seats). Null = never captured. */
    actualSupplierNetMinor: Paise.nullable().default(null),
    /** The supplier's published fare (airfare + seats). Null = never stated. */
    supplierPublishedMinor: Paise.nullable().default(null),
    /** What the payment gateway charged us to collect this booking (incl its GST). Null = not captured. */
    gatewayFeeMinor: Paise.nullable().default(null),
    supplierCurrency: z.string(),
    currencyCode: z.string(),
  }),
  payment: z.object({
    provider: z.string().nullable(),
    orderId: z.string().nullable(),
    paymentId: z.string().nullable(),
    paidAt: z.string().nullable(),
  }),
  /**
   * The add-ons on this booking, one line each with its own price, read from
   * the supplier ticketing request - a seat costs what the airline charged for
   * that specific seat, so the total "Add-ons" figure breaks down per seat /
   * meal here. Empty when there are none or the breakdown could not be read.
   */
  addons: z
    .array(
      z.object({
        paxName: z.string(),
        kind: z.string(),
        code: z.string(),
        description: z.string().nullable(),
        pricePaise: Paise,
      }),
    )
    .default([]),
  /**
   * Present only while `ON_HOLD`. `opsReason` is the supplier's real reason;
   * `nextReminderAt` is when admins are nudged again. Nothing expires on its
   * own - the hold waits for a decision.
   */
  hold: z
    .object({
      since: z.string(),
      nextReminderAt: z.string(),
      opsReason: z.string().nullable(),
      attempts: z.number().int(),
    })
    .nullable(),
  /**
   * The latest cancellation raised with the supplier, if any. The money
   * figures are the supplier's: what it charged us and what it credits back
   * to our account. Null until a request has been settled or refused.
   */
  cancellation: z
    .object({
      status: z.enum(['PENDING', 'PROCESSED', 'REJECTED']),
      scope: z.string(),
      requestedAt: z.string(),
      resolvedAt: z.string().nullable(),
      requestedByAdmin: z.boolean(),
      quotedChargePaise: Paise.nullable(),
      actualChargePaise: Paise.nullable(),
      supplierCreditPaise: Paise.nullable(),
      creditNoteNo: z.string().nullable(),
      rejectionReason: z.string().nullable(),
    })
    .nullable(),
  flight: z
    .object({
      pnr: z.string().nullable(),
      tboBookingId: z.string().nullable(),
      ticketNumbers: z.array(z.string()),
      isLcc: z.boolean(),
      journeyType: z.number().int(),
      cabinClass: z.string(),
      lastTicketDate: z.string().nullable(),
      /** Verbatim supplier snapshot, one array per leg. */
      legs: z.array(z.array(z.record(z.string(), z.unknown()))),
    })
    .nullable(),
  hotel: z
    .object({
      hotelCode: z.string(),
      hotelName: z.string(),
      address: z.string().nullable(),
      starRating: z.number().int().nullable(),
      checkIn: z.string(),
      checkOut: z.string(),
      nights: z.number().int(),
      guestNationality: z.string(),
      rooms: z.array(HotelBookedRoomSchema),
      confirmationNo: z.string().nullable(),
      supplierBookingId: z.string().nullable(),
      supplierStatus: z.string().nullable(),
      specialRequests: z.string().nullable(),
      isRefundable: z.boolean(),
      lastCancellationDate: z.string().nullable(),
    })
    .nullable(),
  passengers: z.array(TravelBookingAdminPassengerSchema),
  guests: z.array(TravelBookingAdminGuestSchema),
  events: z.array(TravelBookingAdminEventSchema),
});
export type TravelBookingAdminDetail = z.infer<typeof TravelBookingAdminDetailSchema>;

/**
 * What "Book now" shows before it does anything.
 *
 * `available: false` means the supplier no longer has the seat / room at all
 * and the only honest action is a refund. Otherwise `deltaPaise` is the money
 * question: positive means the supplier now wants more than the guest paid,
 * and confirming absorbs it from margin.
 */
export const TravelBookingAdminRequoteSchema = z.object({
  bookingId: z.string(),
  status: TravelBookingAdminStatusSchema,
  available: z.boolean(),
  reason: z.string().nullable(),
  paidPaise: Paise,
  newSellPaise: Paise.nullable(),
  deltaPaise: Paise.nullable(),
  currencyCode: z.string(),
  supplierNetBeforeMinor: Paise,
  supplierNetAfterMinor: Paise.nullable(),
  supplierCurrency: z.string(),
  /** True when the original fare/rate session had died and a fresh search found the same itinerary. */
  refreshedFromSearch: z.boolean(),
  /** A non-LCC PNR still held from the first attempt; ticketing will reuse it. */
  heldPnr: z.string().nullable(),
  /** Anything the operator should read before confirming. */
  notes: z.array(z.string()),
  quotedAt: z.string(),
});
export type TravelBookingAdminRequote = z.infer<typeof TravelBookingAdminRequoteSchema>;

export const TravelBookingAdminIssueInputSchema = z.object({
  /**
   * The delta the operator saw and agreed to. The server re-quotes and
   * refuses with 409 `PRICE_CHANGED` if the fresh delta is larger, so a price
   * that moved between the dialog and the click is never silently absorbed.
   */
  acceptedDeltaPaise: Paise,
});
export type TravelBookingAdminIssueInput = z.infer<typeof TravelBookingAdminIssueInputSchema>;

export const TravelBookingAdminActionResultSchema = z.object({
  id: z.string(),
  status: TravelBookingAdminStatusSchema,
  message: z.string(),
});
export type TravelBookingAdminActionResult = z.infer<typeof TravelBookingAdminActionResultSchema>;

/**
 * What "Resend confirmation" returns. `sent` is the engine's answer, not a
 * delivery receipt: true means the notification was accepted and queued;
 * false means nothing went out and `message` says why (no contact email, the
 * engine refused, ...).
 */
export const TravelBookingAdminResendResultSchema = TravelBookingAdminActionResultSchema.extend({
  sent: z.boolean(),
});
export type TravelBookingAdminResendResult = z.infer<typeof TravelBookingAdminResendResultSchema>;

export const TravelBookingAdminSummarySchema = z.object({
  /** Bookings waiting in ON_HOLD right now. */
  actionNeeded: z.number().int(),
  /** Guest money sitting behind those holds. */
  heldPaise: Paise,
  /** When the longest-waiting hold began. Null when nothing is held. */
  oldestHoldSince: z.string().nullable(),
  /** The next admin reminder due across every hold. Null when nothing is held. */
  nextHoldExpiresAt: z.string().nullable(),
  confirmedToday: z.number().int(),
  failedToday: z.number().int(),
  /**
   * Total profit on every CONFIRMED booking, in paise, EXCLUDING GST: our own
   * markup plus the agency discount the supplier gives us (published - what TBO
   * actually billed). GST is collected on the traveller's behalf, not profit,
   * so it is left out. Zero when nothing is confirmed.
   */
  totalProfitPaise: Paise,
  currencyCode: z.string(),
});
export type TravelBookingAdminSummary = z.infer<typeof TravelBookingAdminSummarySchema>;

/** The flight e-ticket PDF for a booking, as a URL the admin can open. */
export const TravelBookingAdminEticketSchema = z.object({
  url: z.string().url(),
});
export type TravelBookingAdminEticket = z.infer<typeof TravelBookingAdminEticketSchema>;

/** Result of the gateway-fee backfill: how many paid bookings were scanned and filled. */
export const TravelBookingAdminGatewayFeeBackfillSchema = z.object({
  scanned: z.number().int(),
  updated: z.number().int(),
});
export type TravelBookingAdminGatewayFeeBackfill = z.infer<
  typeof TravelBookingAdminGatewayFeeBackfillSchema
>;

export const TravelBookingAdminPassportRevealSchema = z.object({
  passportNumber: z.string().nullable(),
  panNumber: z.string().nullable(),
});
export type TravelBookingAdminPassportReveal = z.infer<
  typeof TravelBookingAdminPassportRevealSchema
>;

/**
 * Show enough of a document number to recognise it, never enough to use it.
 * "X1234567" -> "*****567". Anything three characters or shorter is fully
 * masked - there is nothing to recognise it by.
 */
export function maskDocumentNumber(value: string | null | undefined): string | null {
  if (!value) return null;
  const v = value.trim();
  if (!v) return null;
  if (v.length <= 3) return '*'.repeat(v.length);
  return `${'*'.repeat(v.length - 3)}${v.slice(-3)}`;
}

/** Human labels for every status, shared by the list, the detail and the badge. */
export const TRAVEL_BOOKING_ADMIN_STATUS_LABELS: Readonly<
  Record<TravelBookingAdminStatus, string>
> = {
  DRAFT: 'Draft',
  PRICED: 'Priced',
  PAX_ENTERED: 'Details entered',
  PAYMENT_PENDING: 'Payment pending',
  PAID: 'Paid',
  BOOKING: 'Booking',
  ON_HOLD: 'Action needed',
  CONFIRMED: 'Confirmed',
  BOOK_FAILED: 'Failed',
  CANCELLATION_REQUESTED: 'Cancellation requested',
  CANCELLED: 'Cancelled',
  REFUNDED: 'Refunded',
  EXPIRED: 'Expired',
  HELD: 'Locked (balance due)',
};

// ===========================================================================
// Refunds
// ===========================================================================

/**
 * Everything an operator reads before typing a refund amount.
 *
 * Nothing here decides for them. The guest's payment is broken into its
 * lines, the supplier's own figures (what it charged, what it credits back)
 * are shown when a cancellation has settled, and `suggestedRefundPaise` is
 * only a starting point - the confirmed amount is whatever the operator
 * enters, bounded by `maxRefundPaise`.
 */
export const TravelBookingAdminRefundQuoteSchema = z.object({
  bookingId: z.string(),
  reference: z.string(),
  product: TravelBookingAdminProductSchema,
  status: TravelBookingAdminStatusSchema,
  currencyCode: z.string(),
  guestName: z.string(),
  /** The guest's payment, line by line. */
  guestPaidPaise: Paise,
  basePaise: Paise,
  taxPaise: Paise,
  ssrPaise: Paise,
  markupPaise: Paise,
  convenienceFeePaise: Paise,
  gstPaise: Paise,
  tcsPaise: Paise,
  /** Rounding to the whole rupee, derived server-side; 0 for older bookings. */
  roundOffPaise: Paise,
  /** What the supplier was to be paid, in its own currency. */
  supplierNetMinor: Paise,
  supplierCurrency: z.string(),
  /** Supplier-side settlement of the latest cancellation, when there is one. */
  supplierChargePaise: Paise.nullable(),
  supplierCreditPaise: Paise.nullable(),
  /** Null when no cancellation was raised; PENDING while the supplier is still deciding. */
  cancellationStatus: z.enum(['PENDING', 'PROCESSED', 'REJECTED']).nullable(),
  alreadyRefundedPaise: Paise,
  /** What can still be returned: paid minus already refunded. */
  maxRefundPaise: Paise,
  /** A starting figure, or null when the operator must decide from scratch. */
  suggestedRefundPaise: Paise.nullable(),
  /** A ticket exists on at least one leg of this failed flight. */
  isPartlyIssued: z.boolean(),
  paymentProvider: z.string().nullable(),
  paymentId: z.string().nullable(),
  /** False when nothing can be refunded; `blockers` says why. */
  refundable: z.boolean(),
  blockers: z.array(z.string()),
  /** Things worth reading before confirming. */
  notes: z.array(z.string()),
});
export type TravelBookingAdminRefundQuote = z.infer<typeof TravelBookingAdminRefundQuoteSchema>;

export const TravelBookingAdminRefundInputSchema = z.object({
  /** Whole paise, positive, at most the quote's `maxRefundPaise`. */
  amountPaise: z.string().regex(/^\d+$/, 'Whole paise only'),
  note: z.string().trim().max(500).optional(),
});
export type TravelBookingAdminRefundInput = z.infer<typeof TravelBookingAdminRefundInputSchema>;

export const TravelBookingAdminRefundResultSchema = TravelBookingAdminActionResultSchema.extend({
  /** Cumulative, after this refund. */
  refundedPaise: Paise,
  refundId: z.string().nullable(),
  /** `PENDING` is success - card refunds settle over days. */
  refundStatus: z.enum(['PENDING', 'PROCESSED']).nullable(),
});
export type TravelBookingAdminRefundResult = z.infer<typeof TravelBookingAdminRefundResultSchema>;

// ===========================================================================
// Cancellation by an admin
// ===========================================================================

export const TravelBookingAdminCancelQuoteSchema = TravelCancellationQuoteSchema.extend({
  bookingId: z.string(),
  reference: z.string(),
  status: TravelBookingAdminStatusSchema,
  guestPaidPaise: Paise,
  /** True when a request is already with the supplier - confirm is closed. */
  requestPending: z.boolean(),
});
export type TravelBookingAdminCancelQuote = z.infer<typeof TravelBookingAdminCancelQuoteSchema>;

export const TravelBookingAdminCancelInputSchema = z.object({
  reasonCode: CancellationReasonCodeSchema,
  note: z.string().trim().max(300).optional(),
  /** The charge the operator saw. The server refuses with 409 CHARGE_CHANGED if it moved. */
  acknowledgedChargePaise: Paise,
});
export type TravelBookingAdminCancelInput = z.infer<typeof TravelBookingAdminCancelInputSchema>;

// ===========================================================================
// Drop-offs
// ===========================================================================

export const TravelBookingAdminDeleteInputSchema = z.object({
  ids: z.array(z.string().uuid()).min(1).max(100),
});
export type TravelBookingAdminDeleteInput = z.infer<typeof TravelBookingAdminDeleteInputSchema>;

/**
 * What a bulk delete came to. A row that was not safe to delete (it was paid
 * for, or a payment could still land on it) is skipped and named, never
 * silently dropped from the count.
 */
export const TravelBookingAdminDeleteResultSchema = z.object({
  deleted: z.number().int(),
  skipped: z.array(z.object({ id: z.string(), reference: z.string(), reason: z.string() })),
});
export type TravelBookingAdminDeleteResult = z.infer<typeof TravelBookingAdminDeleteResultSchema>;

export const TRAVEL_BOOKING_DROP_OFF_STAGE_LABELS: Readonly<Record<'DETAILS' | 'PAYMENT', string>> =
  {
    DETAILS: 'Details entered',
    PAYMENT: 'Payment started',
  };

// ===========================================================================
// Reports
// ===========================================================================

export const TravelBookingReportQuerySchema = z.object({
  month: z.coerce.number().int().min(1).max(12),
  year: z.coerce.number().int().min(2020).max(2100),
  product: TravelBookingAdminProductSchema.optional(),
});
export type TravelBookingReportQuery = z.infer<typeof TravelBookingReportQuerySchema>;

const RankedRow = z.object({
  label: z.string(),
  count: z.number().int(),
  salesPaise: Paise,
});

/**
 * One month of website bookings, in money and in counts.
 *
 * Every money figure is in `currencyCode` (the sell currency) except the
 * supplier cost, which is only summed where the supplier billed in that same
 * currency - `supplierCostIncomplete` says when some of it was left out
 * rather than converted at a rate nobody recorded.
 */
export const TravelBookingReportSchema = z.object({
  period: z.object({
    from: z.string(),
    to: z.string(),
    month: z.number().int(),
    year: z.number().int(),
  }),
  currencyCode: z.string(),
  totals: z.object({
    /** Bookings that were paid for in the period. */
    paid: z.number().int(),
    confirmed: z.number().int(),
    onHoldNow: z.number().int(),
    failed: z.number().int(),
    cancelled: z.number().int(),
    refunded: z.number().int(),
    grossSalesPaise: Paise,
    basePaise: Paise,
    taxPaise: Paise,
    markupPaise: Paise,
    convenienceFeePaise: Paise,
    gstPaise: Paise,
    refundedPaise: Paise,
    absorbedDeltaPaise: Paise,
    supplierCostPaise: Paise,
    supplierCostIncomplete: z.boolean(),
    /** Gross sales minus supplier cost minus GST collected minus refunds minus absorbed deltas. */
    netMarginPaise: Paise.nullable(),
    averageBookingPaise: Paise,
    travellers: z.number().int(),
  }),
  byProduct: z.array(
    z.object({
      product: TravelBookingAdminProductSchema,
      paid: z.number().int(),
      confirmed: z.number().int(),
      cancelled: z.number().int(),
      grossSalesPaise: Paise,
      markupPaise: Paise,
      refundedPaise: Paise,
    }),
  ),
  /** Where checkouts started in the period ended up. */
  funnel: z.object({
    started: z.number().int(),
    priced: z.number().int(),
    detailsEntered: z.number().int(),
    paymentStarted: z.number().int(),
    paid: z.number().int(),
    confirmed: z.number().int(),
  }),
  holds: z.object({
    placed: z.number().int(),
    bookedByAdmin: z.number().int(),
    refunded: z.number().int(),
    stillWaiting: z.number().int(),
    averageMinutesToResolve: z.number().nullable(),
    absorbedDeltaPaise: Paise,
  }),
  topRoutes: z.array(RankedRow),
  topAirlines: z.array(RankedRow),
  topHotels: z.array(RankedRow),
  topCountries: z.array(RankedRow),
  topDestinations: z.array(RankedRow),
  byDay: z.array(z.object({ date: z.string(), paid: z.number().int(), salesPaise: Paise })),
  /** Days between booking and travel, averaged over paid bookings. */
  averageLeadTimeDays: z.number().nullable(),
  averageNights: z.number().nullable(),
  cabinMix: z.array(z.object({ label: z.string(), count: z.number().int() })),
  lccShare: z.number().nullable(),
  refundableShare: z.number().nullable(),
});
export type TravelBookingReport = z.infer<typeof TravelBookingReportSchema>;
